import { useEffect, useCallback, useRef } from 'react';

interface KeyboardHandler {
    key: string;
    handler: (event: KeyboardEvent) => void;
    preventDefault?: boolean;
    stopPropagation?: boolean;
    modifiers?: {
        ctrl?: boolean;
        shift?: boolean;
        alt?: boolean;
        meta?: boolean;
    };
}

export interface UseKeyboardNavigationOptions {
    handlers: KeyboardHandler[];
    enabled?: boolean;
    target?: 'window' | 'document' | HTMLElement | null;
    capture?: boolean;
}

/** Registers keydown handlers on the target while enabled; returns nothing. */
export const useKeyboardNavigation = (
    options: UseKeyboardNavigationOptions
) => {
    const {
        handlers: initialHandlers,
        enabled = true,
        target = 'window',
        capture = false
    } = options;

    const handlersRef = useRef<Map<string, KeyboardHandler>>(new Map());
    const enabledRef = useRef(enabled);
    const isListeningRef = useRef(false);

    const createHandlerKey = useCallback((handler: KeyboardHandler): string => {
        const modifiers = handler.modifiers || {};
        const modifierStr = [
            modifiers.ctrl ? 'ctrl' : '',
            modifiers.shift ? 'shift' : '',
            modifiers.alt ? 'alt' : '',
            modifiers.meta ? 'meta' : ''
        ].filter(Boolean).join('+');
        
        return modifierStr ? `${modifierStr}+${handler.key}` : handler.key;
    }, []);

    useEffect(() => {
        const handlerMap = new Map();
        initialHandlers.forEach(handler => {
            const key = createHandlerKey(handler);
            handlerMap.set(key, handler);
        });
        handlersRef.current = handlerMap;
    }, [initialHandlers, createHandlerKey]);

    const checkModifiers = useCallback((
        event: KeyboardEvent, 
        required: KeyboardHandler['modifiers'] = {}
    ): boolean => {
        return (
            !!event.ctrlKey === !!required.ctrl &&
            !!event.shiftKey === !!required.shift &&
            !!event.altKey === !!required.alt &&
            !!event.metaKey === !!required.meta
        );
    }, []);

    const handleKeyDown = useCallback((event: Event) => {
        const keyboardEvent = event as KeyboardEvent;
        if (!enabledRef.current) {
            return;
        }

        let matchedHandler: KeyboardHandler | undefined;

        for (const handler of handlersRef.current.values()) {
            if (handler.key === keyboardEvent.key && checkModifiers(keyboardEvent, handler.modifiers)) {
                matchedHandler = handler;
                break;
            }
        }

        if (matchedHandler) {
            if (matchedHandler.preventDefault !== false) {
                keyboardEvent.preventDefault();
            }
            if (matchedHandler.stopPropagation) {
                keyboardEvent.stopPropagation();
            }
            
            matchedHandler.handler(keyboardEvent);
        }
    }, [checkModifiers]);

    const getTarget = useCallback(() => {
        if (target === 'window') {
            return window;
        }
        if (target === 'document') {
            return document;
        }
        if (target instanceof HTMLElement) {
            return target;
        }
        return window; // fallback
    }, [target]);

    const addListener = useCallback(() => {
        if (isListeningRef.current) {
            return;
        }
        
        const targetElement = getTarget();
        targetElement.addEventListener('keydown', handleKeyDown, capture);
        isListeningRef.current = true;
    }, [getTarget, handleKeyDown, capture]);

    const removeListener = useCallback(() => {
        if (!isListeningRef.current) {
            return;
        }
        
        const targetElement = getTarget();
        targetElement.removeEventListener('keydown', handleKeyDown, capture);
        isListeningRef.current = false;
    }, [getTarget, handleKeyDown, capture]);

    useEffect(() => {
        if (enabledRef.current) {
            addListener();
        } else {
            removeListener();
        }

        return () => {
            removeListener();
        };
    }, [addListener, removeListener]);

    useEffect(() => {
        enabledRef.current = enabled;
        if (enabled) {
            addListener();
        } else {
            removeListener();
        }
    }, [enabled, addListener, removeListener]);
};

// Convenience hooks for common keyboard patterns

export const useEscapeKey = (handler: (event: KeyboardEvent) => void, enabled = true) => {
    return useKeyboardNavigation({
        handlers: [{ key: 'Escape', handler }],
        enabled
    });
};
